/**
 * One place to decide how a linked pull request looks, so the compact chip on a
 * scope card and the fuller row in the drawer agree on colour and wording. The
 * state a reader cares about is a blend of the PR's lifecycle (draft/open/merged/
 * closed) and its verdicts (review + CI): a red CI on an open PR should read red,
 * not blue.
 */

/**
 * The MUI colour and short label for a PR's overall state.
 * @param {object} pr a serialized ScopePullRequest
 * @returns {{ color: string, label: string }}
 */
export function prChipStyle(pr = {}) {
  if (pr.state === 'merged') return { color: 'success', label: 'merged' };
  if (pr.state === 'closed') return { color: 'default', label: 'closed' };

  // Open from here on: needs your review takes priority (amber/warning), then
  // let a failing check or a change request dominate, then a clean approval,
  // then draft, then plain open.
  if (pr.needsYourReview) {
    return { color: 'warning', label: 'review!' };
  }
  if (pr.checksState === 'failure' || pr.reviewState === 'changes_requested') {
    return { color: 'error', label: pr.checksState === 'failure' ? 'CI failing' : 'changes' };
  }
  if (pr.reviewState === 'approved') return { color: 'success', label: 'approved' };
  if (pr.isDraft) return { color: 'default', label: 'draft' };
  if (pr.reviewState === 'pending') return { color: 'info', label: 'in review' };
  return { color: 'info', label: 'open' };
}

/**
 * A one-line human summary of a PR's review + CI state for tooltips and rows.
 */
export function prStateSummary(pr = {}) {
  const parts = [];
  if (pr.state === 'merged') parts.push('Merged');
  else if (pr.state === 'closed') parts.push('Closed');
  else if (pr.isDraft) parts.push('Draft');
  else parts.push('Open');

  if (pr.reviewState === 'approved') parts.push('approved');
  else if (pr.reviewState === 'changes_requested') parts.push('changes requested');
  else if (pr.reviewState === 'pending') parts.push('review pending');

  if (pr.checksState === 'success') parts.push('checks passing');
  else if (pr.checksState === 'failure') parts.push('checks failing');
  else if (pr.checksState === 'pending') parts.push('checks running');

  return parts.join(' · ');
}

/**
 * The short sha and first line of a commit's message, truncated so a drawer row
 * stays one line. The server already reduces the message to its first line and
 * computes shortSha, so this mostly guards length and missing fields.
 * @param {object} commit a serialized ScopeCommit
 * @returns {{ shortSha: string, message: string }}
 */
export function commitSummary(commit = {}, maxLength = 72) {
  const shortSha = commit.shortSha || (commit.sha ? commit.sha.slice(0, 7) : '');
  const firstLine = (commit.message || '').split('\n')[0];
  const message = firstLine.length > maxLength
    ? `${firstLine.slice(0, maxLength - 1)}\u2026`
    : firstLine;
  return { shortSha, message };
}
